import { HumanCallAvatar } from "@/pages/agent-interfaces/live-agent/components/HumanCallAvatar";
import { HumanCallBar } from "@/pages/agent-interfaces/live-agent/components/HumanCallBar";
import { HumanHandoffButton } from "@/pages/agent-interfaces/live-agent/components/HumanHandoffButton";
import { VoiceSessionActionRow } from "@/pages/agent-interfaces/live-agent/components/VoiceSessionActionRow";
import { VoiceAgentAvatar, VoiceAgentControls } from "@/components/VoiceAgent";
import { cn } from "@/lib/utils";
import { AgentPageProps } from "@/types/agent/agent-page";
import { InfoLinksConfig } from "@/types/agent/info-links";
import type { WidgetChatButtonStyle } from "@/types/ui/branding";
import { StudioActionCluster } from "./StudioActionCluster";
import { STAGE_ANCHOR_CLASS } from "@/pages/agent-interfaces/live-agent/useStageAnchorCentering";
import type { VoiceBridgeStatus as BridgeStatus } from "@/services/integrations/twilio/VoiceBridgeService";

/**
 * The avatar + status message + start/end control + mic/chat/voice action row
 * + human-handoff button/bar cluster. Rendered on the template branch, the
 * mobile layout, and the desktop layout — each with slightly different sizing
 * and a couple of behavior toggles, expressed here as props rather than three
 * separately maintained copies of the same JSX.
 */
export interface AgentCoreProps {
  brand: string;
  agent: AgentPageProps["agentPageData"] | null;
  isEmbedMode: boolean;
  isMobile: boolean;

  statusMessage: string;
  /** Hides the status message entirely (desktop chat-focus mode, or the
   *  Studio-only fullPageStyle.layout.welcomePosition === "hidden" toggle). */
  hideStatusMessage?: boolean;
  /** Studio-only: text color override for the status/welcome message, from
   *  fullPageStyle.welcomeTextColor. */
  statusMessageColor?: string | null;
  /** Studio-only: renders the status/welcome message after the avatar/CTA
   *  cluster instead of before it, from fullPageStyle.layout.welcomePosition. */
  statusMessageBelow?: boolean;

  voiceAgent: {
    speaking: boolean;
    listening: boolean;
    thinking: boolean;
    processing: boolean;
    searching: boolean;
    retrieving: boolean;
    isConnecting: boolean;
    connected: boolean;
  };
  onAvatarClick: () => void;

  isVoiceBridgeEnabled: boolean;
  voiceBridge: {
    bridgeStatus: BridgeStatus;
    muted: boolean;
    destinationLabel?: string | null;
    toggleMute: () => void;
  };
  showHumanHandoffButton: boolean;
  onOpenHandoffPrompt: () => void;
  onHangupHandoff: () => void;

  /** Fixed pixel size for the avatar (desktop chat-focus mode shrinks it). Omit for the component's default sizing. */
  avatarSize?: number;
  /** Embed-only avatar cover image override. */
  embedAvatarImageUrl?: string | null;
  embedAvatarShowLogoOverlay?: boolean;
  embedAvatarSizeScale?: number;

  /** Embed-only CTA style overrides. */
  embedCtaShape?: "pill" | "rounded" | "square";
  embedCtaStyle?: {
    color?: string;
    color2?: string;
    textColor?: string;
    sizeScale?: number;
  } | null;
  /** Desktop chat-focus mode shrinks the CTA to a compact "Start"/"End" pill. */
  ctaCompact?: boolean;
  ctaClassName?: string;

  isTranscriptEnabled: boolean;
  isMicMuted: boolean;
  onToggleMicMute: () => void;
  isAgentVoiceMuted: boolean;
  onToggleAgentVoice: () => void;
  isPaused: boolean;
  onTogglePause: () => void;
  onOpenChat: () => void;
  hasResources?: boolean;
  onOpenResources?: () => void;
  onOpenDetails?: () => void;
  /** VoiceSessionActionRow's own info-popover trigger; the mobile/desktop
   * layouts surface "About"/details elsewhere and turn this off. */
  showInfoActions?: boolean;
  onAddImage: (source: "library" | "camera") => void;
  onSummaryCaptured: (email: string) => void;
  embedActionButtonStyle?: unknown;

  /** Renders the action row inline in this component (template branch). Mobile
   * passes its own row via MobileLiveAgentLayout's rightInlineIcons instead. */
  showActionRow?: boolean;

  /** Studio-only alternate layout: Talk/Chat entry + Mic/Stop/More row,
   * matching the live-agent page's own control set. */
  useAlternateCluster?: boolean;
  chatOpen?: boolean;
  isTextOnlyMode?: boolean;
  /** Studio-only: starts a text conversation from the idle Talk/Chat row. */
  onStartText?: () => void;
  phoneNumber?: string | null;
  onCallGenie?: () => void;
  mailboxAddress?: string | null;
  onEmailGenie?: () => void;
  /** Studio-only: Chat button color/text overrides for the alternate cluster. */
  chatButtonStyle?: WidgetChatButtonStyle | null;

  /** Persistent inline "having trouble connecting?" recovery card — shown
   *  under the CTA/action row after repeated quick, reply-less sessions, or
   *  a failed start attempt. Distinct from the manually-triggered full
   *  VoiceTroubleSheet. Omit to render nothing (matches LiveAgentPage, which
   *  also hides it in embed/demo and while the cluster is compact). */
  voiceTroubleCard?: React.ReactNode;

  /** What the Genie just shared (showRelatedMedia), floated under the avatar
   *  while the transcript is closed. The caller owns visibility. */
  sharedMediaCard?: React.ReactNode;
}

export function AgentCore({
  brand,
  agent,
  isEmbedMode,
  isMobile,
  statusMessage,
  hideStatusMessage = false,
  statusMessageColor,
  statusMessageBelow = false,
  voiceAgent,
  onAvatarClick,
  isVoiceBridgeEnabled,
  voiceBridge,
  showHumanHandoffButton,
  onOpenHandoffPrompt,
  onHangupHandoff,
  avatarSize,
  embedAvatarImageUrl,
  embedAvatarShowLogoOverlay,
  embedAvatarSizeScale,
  embedCtaShape,
  embedCtaStyle,
  ctaCompact = false,
  ctaClassName,
  isTranscriptEnabled,
  isMicMuted,
  onToggleMicMute,
  isAgentVoiceMuted,
  onToggleAgentVoice,
  isPaused,
  onTogglePause,
  onOpenChat,
  hasResources = false,
  onOpenResources,
  onOpenDetails,
  showInfoActions = true,
  onAddImage,
  onSummaryCaptured,
  embedActionButtonStyle,
  showActionRow = true,
  useAlternateCluster = false,
  chatOpen = false,
  isTextOnlyMode = false,
  onStartText,
  phoneNumber,
  onCallGenie,
  mailboxAddress,
  onEmailGenie,
  chatButtonStyle,
  voiceTroubleCard,
  sharedMediaCard,
}: AgentCoreProps) {
  return (
    <>
      {!hideStatusMessage && !statusMessageBelow && (
        <p
          className={cn(
            "text-center font-medium leading-[1.4] px-4 py-2 bg-transparent border-none rounded-xl",
            !statusMessageColor && "text-[#666]",
            isEmbedMode
              ? "text-[0.9rem] mb-7 max-w-[260px]"
              : isMobile
                ? "text-[0.95rem] mb-6 max-w-[280px]"
                : "text-[1.05rem] mb-8 max-w-[300px]",
          )}
          style={statusMessageColor ? { color: statusMessageColor } : undefined}
        >
          {statusMessage}
        </p>
      )}

      {isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" ? (
        <HumanCallAvatar
          className={STAGE_ANCHOR_CLASS}
          brandColor={brand}
          status={voiceBridge.bridgeStatus}
          embedded={isEmbedMode}
          size={avatarSize}
        />
      ) : (
        <VoiceAgentAvatar
          className={STAGE_ANCHOR_CLASS}
          brandColor={brand}
          customBranding={agent?.branding}
          coverImageUrl={embedAvatarImageUrl}
          showLogoOverlay={embedAvatarShowLogoOverlay}
          sizeScale={embedAvatarSizeScale}
          onClick={onAvatarClick}
          speaking={voiceAgent.speaking}
          listening={voiceAgent.listening}
          thinking={voiceAgent.thinking}
          processing={voiceAgent.processing}
          searching={voiceAgent.searching}
          retrieving={voiceAgent.retrieving}
          isConnecting={voiceAgent.isConnecting}
          connected={voiceAgent.connected}
          embedded={isEmbedMode}
          size={avatarSize}
        />
      )}

      {!useAlternateCluster && (
        <VoiceAgentControls
          connected={voiceAgent.connected}
          isConnecting={voiceAgent.isConnecting}
          onToggle={onAvatarClick}
          brandColor={brand}
          secondaryColor={agent?.branding?.secondaryColor}
          gradient={isEmbedMode}
          buttonText={{
            start: ctaCompact ? "Start" : "Start Conversation",
            end: ctaCompact ? "End" : "End Conversation",
          }}
          className={ctaClassName ?? (isEmbedMode ? "mt-5" : "mt-6 md:mt-8")}
          compact={ctaCompact}
          shape={embedCtaShape}
          colorOverride={embedCtaStyle?.color}
          colorOverride2={embedCtaStyle?.color2}
          textColorOverride={embedCtaStyle?.textColor}
          sizeScale={embedCtaStyle?.sizeScale}
        />
      )}

      {voiceTroubleCard}

      {showActionRow && useAlternateCluster && (
        <StudioActionCluster
          brandColor={brand}
          secondaryColor={agent?.branding?.secondaryColor}
          isMobile={isMobile}
          voiceConnected={voiceAgent.connected}
          isConnecting={voiceAgent.isConnecting}
          onStartVoice={onAvatarClick}
          onStartText={onStartText ?? onOpenChat}
          onEndConversation={onAvatarClick}
          onToggleChat={onOpenChat}
          chatOpen={chatOpen}
          isTextOnlyMode={isTextOnlyMode}
          isMicMuted={isMicMuted}
          onToggleMicMute={onToggleMicMute}
          isAgentVoiceMuted={isAgentVoiceMuted}
          onToggleAgentVoice={onToggleAgentVoice}
          isPaused={isPaused}
          onTogglePause={onTogglePause}
          onAddImage={onAddImage}
          onTalkToHuman={
            showHumanHandoffButton && voiceBridge.bridgeStatus === "idle" ? onOpenHandoffPrompt : undefined
          }
          phoneNumber={phoneNumber}
          onCallGenie={onCallGenie}
          mailboxAddress={mailboxAddress}
          onEmailGenie={onEmailGenie}
          actionButtonStyle={embedActionButtonStyle}
          ctaButtonStyle={embedCtaStyle}
          ctaShape={embedCtaShape}
          chatButtonStyle={chatButtonStyle}
        />
      )}

      {showActionRow && !useAlternateCluster && (
        <VoiceSessionActionRow
          brandColor={brand}
          isMobile={isMobile}
          isTranscriptFeatureEnabled={isTranscriptEnabled || isEmbedMode}
          voiceConnected={voiceAgent.connected}
          isMicMuted={isMicMuted}
          onToggleMicMute={onToggleMicMute}
          isAgentVoiceMuted={isAgentVoiceMuted}
          onToggleAgentVoice={onToggleAgentVoice}
          isPaused={isPaused}
          onTogglePause={onTogglePause}
          actionButtonStyle={embedActionButtonStyle}
          onOpenChat={onOpenChat}
          infoTitle={`About ${agent?.name || "this Genie"}`}
          infoDescription={agent?.description || null}
          hasResources={hasResources}
          onOpenResources={onOpenResources}
          infoLinks={agent?.page?.info_links as InfoLinksConfig | null}
          qrLogoUrl={agent?.branding?.logoUrl || null}
          secondaryColor={agent?.branding?.secondaryColor}
          showInfoActions={showInfoActions}
          onOpenDetails={onOpenDetails}
          onAddImage={onAddImage}
          agentId={agent?.id ?? undefined}
          agentUserId={agent?.user_id ?? undefined}
          onSummaryCaptured={onSummaryCaptured}
        />
      )}

      {/* Always below the action row, so the controls stay right under the
          genie however much has been shared. */}
      {sharedMediaCard}

      {!hideStatusMessage && statusMessageBelow && (
        <p
          className={cn(
            "text-center font-medium leading-[1.4] px-4 py-2 mt-3 bg-transparent border-none rounded-xl",
            !statusMessageColor && "text-[#666]",
            isEmbedMode
              ? "text-[0.9rem] max-w-[260px]"
              : isMobile
                ? "text-[0.95rem] max-w-[280px]"
                : "text-[1.05rem] max-w-[300px]",
          )}
          style={statusMessageColor ? { color: statusMessageColor } : undefined}
        >
          {statusMessage}
        </p>
      )}

      {!useAlternateCluster && showHumanHandoffButton && voiceBridge.bridgeStatus === "idle" && (
        <div className="mt-3 flex justify-center">
          <HumanHandoffButton
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            onClick={onOpenHandoffPrompt}
          />
        </div>
      )}

      {isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" && (
        <HumanCallBar
          brandColor={brand}
          secondaryColor={agent?.branding?.secondaryColor}
          status={voiceBridge.bridgeStatus}
          muted={voiceBridge.muted}
          destinationLabel={voiceBridge.destinationLabel}
          onToggleMute={voiceBridge.toggleMute}
          onHangup={onHangupHandoff}
        />
      )}
    </>
  );
}

export default AgentCore;
